import { log } from '../services/production-logger';

/**
 * Centralized environment variables configuration
 * Provides consistent access to environment variables across platforms with security validation
 * 
 * SECURITY NOTE: This file contains NO hardcoded secrets. All sensitive values must be provided
 * via environment variables. See .env.example for required variables.
 */
import './load-env'; // Load environment variables for web builds
import { Platform } from 'react-native';

// Environment variable validation
const requiredEnvVars = [
  'EXPO_PUBLIC_FIREBASE_API_KEY',
  'EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN', 
  'EXPO_PUBLIC_FIREBASE_PROJECT_ID',
  'EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET',
  'EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID',
  'EXPO_PUBLIC_FIREBASE_APP_ID',
] as const;

const sensitiveEnvVars = [
  'EXPO_PUBLIC_OPENAI_API_KEY',
  'EXPO_PUBLIC_NVIDIA_API_KEY',
] as const;

// Validate required environment variables
const validateEnvironment = () => {
  const missing = requiredEnvVars.filter(envVar => !process.env[envVar]);
  if (missing.length > 0) {
    // In development web builds, this is expected as env vars might not be injected yet
    if (Platform.OS === 'web' && __DEV__) {
      log.warn('⚠️ Missing environment variables in web build:', missing);
      log.warn('📝 This is expected during development. Environment variables will be injected at build time.');
    } else {
      log.error('❌ Missing required environment variables:', missing);
      log.error('📝 Please check your .env file against .env.example');
    }
  }

  const missingSensitive = sensitiveEnvVars.filter(envVar => !process.env[envVar]);
  if (missingSensitive.length > 0) {
    log.warn('⚠️ Missing sensitive environment variables:', missingSensitive);
    log.warn('🔑 Some features may not work without proper API keys');
  }

  return missing.length === 0;
};

// Centralized access to all environment variables - NO HARDCODED FALLBACKS
export const env = {
  // Firebase config - REQUIRED for app to function
  FIREBASE_API_KEY: process.env.EXPO_PUBLIC_FIREBASE_API_KEY || '',
  FIREBASE_AUTH_DOMAIN: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN || '',
  FIREBASE_PROJECT_ID: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID || '',
  FIREBASE_STORAGE_BUCKET: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET || '',
  FIREBASE_MESSAGING_SENDER_ID: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '',
  FIREBASE_APP_ID: process.env.EXPO_PUBLIC_FIREBASE_APP_ID || '',
  FIREBASE_MEASUREMENT_ID: process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID || '',

  // AI Service config - Required for invoice processing
  OPENAI_API_KEY: process.env.EXPO_PUBLIC_OPENAI_API_KEY || '',
  OPENAI_MODEL: process.env.EXPO_PUBLIC_OPENAI_MODEL || 'gpt-4o-mini',
  NVIDIA_API_KEY: process.env.EXPO_PUBLIC_NVIDIA_API_KEY || '',
  DASHSCOPE_API_KEY: process.env.EXPO_PUBLIC_DASHSCOPE_API_KEY || '',
  DASHSCOPE_MODEL: process.env.EXPO_PUBLIC_DASHSCOPE_MODEL || 'qwen2.5-vl-7b-instruct',
  AI_SERVICE: process.env.EXPO_PUBLIC_AI_SERVICE || 'dashscope', // Default to best performer
  USE_SECURE_API: process.env.EXPO_PUBLIC_USE_SECURE_API === 'true',

  // Google Cloud config - Required for some features
  GOOGLE_CLOUD_API_KEY: process.env.EXPO_PUBLIC_GOOGLE_CLOUD_API_KEY || '',
  GOOGLE_CLOUD_PROJECT_ID: process.env.EXPO_PUBLIC_GOOGLE_CLOUD_PROJECT_ID || '',
  
  // Google API config - Required for Google services integration
  GOOGLE_API_KEY: process.env.EXPO_PUBLIC_GOOGLE_API_KEY || process.env.EXPO_PUBLIC_FIREBASE_API_KEY || '',
  GOOGLE_WEB_CLIENT_ID: process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID || '',
  
  // Platform-specific Google Client IDs (from Firebase config files)
  // These should be moved to environment variables in production
  GOOGLE_ANDROID_CLIENT_ID: process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID || '',
  GOOGLE_IOS_CLIENT_ID: process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID || '',

  // Stripe config - Optional for payment features
  STRIPE_PUBLISHABLE_KEY: process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || '',

  // EAS config - Optional for builds
  EAS_PROJECT_ID: process.env.EXPO_PUBLIC_EAS_PROJECT_ID || '',

  // Helper to check if running on web
  isWeb: Platform.OS === 'web',

  // Environment validation
  isValid: false, // Will be set by validateEnvironment()
};

// Initialize environment validation
env.isValid = validateEnvironment();

// Debug environment loading
if (__DEV__) {
  log.debug('🔍 [ENV-DEBUG] Raw environment variables:');
  log.debug('EXPO_PUBLIC_AI_SERVICE:', process.env.EXPO_PUBLIC_AI_SERVICE);
  log.debug('EXPO_PUBLIC_DASHSCOPE_API_KEY:', process.env.EXPO_PUBLIC_DASHSCOPE_API_KEY ? 'SET' : 'NOT SET');
  log.debug('EXPO_PUBLIC_DASHSCOPE_MODEL:', process.env.EXPO_PUBLIC_DASHSCOPE_MODEL);
  log.debug('env.AI_SERVICE value:', env.AI_SERVICE);
  
  // Debug Firebase env vars specifically
  log.debug('🔍 [ENV-DEBUG] Firebase environment variables:');
  log.debug('EXPO_PUBLIC_FIREBASE_API_KEY:', process.env.EXPO_PUBLIC_FIREBASE_API_KEY ? 'SET' : 'NOT SET');
  log.debug('EXPO_PUBLIC_FIREBASE_PROJECT_ID:', process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID || 'NOT SET');
  log.debug('EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN:', process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN || 'NOT SET');
}

// Enhanced logging for debugging (without exposing sensitive values)
export const logEnvironmentInfo = () => {
  log.debug('🔧 Environment Configuration Status:', {
    platform: Platform.OS,
    environmentValid: env.isValid,
    firebaseApiKey: env.FIREBASE_API_KEY ? '✓ Set' : '✗ Missing',
    firebaseProjectId: env.FIREBASE_PROJECT_ID ? `✓ ${env.FIREBASE_PROJECT_ID}` : '✗ Missing',
    firebaseAuthDomain: env.FIREBASE_AUTH_DOMAIN ? '✓ Set' : '✗ Missing',
    openaiApiKey: env.OPENAI_API_KEY ? '✓ Set (secure)' : '✗ Missing',
    openaiModel: env.OPENAI_MODEL,
    nvidiaApiKey: env.NVIDIA_API_KEY ? '✓ Set (secure)' : '✗ Missing',
    dashscopeApiKey: env.DASHSCOPE_API_KEY ? '✓ Set (secure)' : '✗ Missing',
    dashscopeModel: env.DASHSCOPE_MODEL,
    aiService: env.AI_SERVICE,
    googleApiKey: env.GOOGLE_API_KEY ? '✓ Set' : '✗ Missing',
    googleCloudApiKey: env.GOOGLE_CLOUD_API_KEY ? '✓ Set' : '✗ Missing',
    googleWebClientId: env.GOOGLE_WEB_CLIENT_ID ? '✓ Set' : '✗ Missing',
    stripePublishableKey: env.STRIPE_PUBLISHABLE_KEY ? '✓ Set' : '✗ Missing',
    easProjectId: env.EAS_PROJECT_ID ? '✓ Set' : '✗ Missing',
  });

  if (!env.isValid) {
    log.error('❌ Environment configuration is invalid. App may not function correctly.');
    log.error('📖 Please refer to .env.example for required environment variables.');
  }
};

// Validate specific service configurations
export const validateServiceConfig = (service: 'firebase' | 'openai' | 'nvidia' | 'dashscope' | 'google'): boolean => {
  switch (service) {
    case 'firebase':
      return !!(env.FIREBASE_API_KEY && env.FIREBASE_PROJECT_ID && env.FIREBASE_AUTH_DOMAIN);
    case 'openai':
      return !!env.OPENAI_API_KEY;
    case 'nvidia':
      return !!env.NVIDIA_API_KEY;
    case 'dashscope':
      return !!env.DASHSCOPE_API_KEY;
    case 'google':
      return !!(env.GOOGLE_API_KEY || env.GOOGLE_WEB_CLIENT_ID);
    default:
      return false;
  }
};

// Get AI service configuration with validation
export const getAIServiceConfig = () => {
  const service = env.AI_SERVICE;
  const isOpenAIValid = validateServiceConfig('openai');
  const isNVIDIAValid = validateServiceConfig('nvidia');
  const isDashScopeValid = validateServiceConfig('dashscope');

  if (service === 'nvidia' && isNVIDIAValid) {
    return { service: 'nvidia', apiKey: env.NVIDIA_API_KEY };
  } else if (service === 'dashscope' && isDashScopeValid) {
    return { service: 'dashscope', apiKey: env.DASHSCOPE_API_KEY, model: env.DASHSCOPE_MODEL };
  } else if (isOpenAIValid) {
    return { service: 'openai', apiKey: env.OPENAI_API_KEY };
  } else {
    log.warn('⚠️ No valid AI service configuration found');
    return { service: null, apiKey: null };
  }
};
